import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FloatingActionButton, Icon } from '../../components/common';
import { PageHeader } from '../../components/layout';
import usePerformanceEvents from '../../hooks/usePerformanceEvents';

/**
 * Event Card Component - Individual performance card
 */
const EventCard = ({ event, index }) => {
  const isToday = () => {
    const today = new Date();
    const eventDate = new Date(event.start);
    return (
      today.getDate() === eventDate.getDate() &&
      today.getMonth() === eventDate.getMonth() &&
      today.getFullYear() === eventDate.getFullYear()
    );
  };

  const isTomorrow = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const eventDate = new Date(event.start);
    return (
      tomorrow.getDate() === eventDate.getDate() &&
      tomorrow.getMonth() === eventDate.getMonth() &&
      tomorrow.getFullYear() === eventDate.getFullYear()
    );
  };

  const getStatusLabel = () => {
    if (isToday()) return { text: 'TODAY', color: 'text-green-400', bg: 'bg-green-400/20', border: 'border-green-400/30' };
    if (isTomorrow()) return { text: 'TOMORROW', color: 'text-gold', bg: 'bg-gold/20', border: 'border-gold/30' };
    return null;
  };

  const status = getStatusLabel();

  // Handle card click - open maps if available
  const handleCardClick = () => {
    if (event.mapsUrl) {
      window.open(event.mapsUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      onClick={handleCardClick}
      className={`group relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/[0.08] to-white/[0.02] border border-white/10 hover:border-primary/30 transition-all duration-300 ${event.mapsUrl ? 'cursor-pointer' : ''}`}
      style={{ animationDelay: `${index * 100}ms` }}
    >
      {/* Glow effect on hover */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary/5 to-neon-pink/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      <div className="relative flex gap-4 p-4">
        {/* Date Badge */}
        <div className="flex flex-col items-center justify-center min-w-[60px] py-2 px-3 bg-gradient-to-br from-primary/20 to-primary/10 rounded-xl border border-primary/20">
          <span className="text-primary text-[11px] font-bold uppercase tracking-wider">{event.dayOfWeek}</span>
          <span className="text-white text-2xl font-black leading-none mt-1">{event.dayOfMonth}</span>
          <span className="text-primary/70 text-[10px] font-medium uppercase tracking-wider mt-0.5">{event.month.slice(0, 3)}</span>
        </div>

        {/* Event Details */}
        <div className="flex-1 flex flex-col gap-1.5 min-w-0">
          {/* Status Badge */}
          {status && (
            <div className={`inline-flex self-start items-center gap-1 px-2 py-0.5 rounded-full ${status.bg} ${status.border} border mb-1`}>
              <span className="relative flex h-1.5 w-1.5">
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${status.color} opacity-75`}></span>
                <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${status.color.replace('text-', 'bg-')}`}></span>
              </span>
              <span className={`text-[10px] font-bold tracking-wider ${status.color}`}>{status.text}</span>
            </div>
          )}

          {/* Venue/Location Name */}
          <h3 className="text-white text-base font-bold leading-snug flex items-center gap-1.5">
            {event.mapsUrl && <Icon name="location_on" size="xs" className="text-neon-pink flex-shrink-0" />}
            <span>{event.venue}</span>
          </h3>

          {/* Show Name */}
          <div className="flex items-center gap-1.5 text-[#90a9cb]">
            <Icon name="music_note" size="xs" className="text-gold/70 flex-shrink-0" />
            <span className="text-sm font-medium">{event.showName}</span>
          </div>

          {/* Time */}
          <div className="flex items-center gap-1.5 text-[#90a9cb]">
            <Icon name="schedule" size="xs" className="text-primary/70 flex-shrink-0" />
            <span className="text-sm font-medium">{event.formattedTime}</span>
          </div>
        </div>

        {/* Chevron Arrow - only show if maps URL exists */}
        {event.mapsUrl && (
          <div className="flex items-center justify-center">
            <Icon name="chevron_right" size="md" className="text-white/30 group-hover:text-primary transition-colors" />
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Fun quotes for when there are no shows
 */
const NO_SHOWS_QUOTES = [
  {
    emoji: "🐼",
    title: "Maybe Panda is on vacation",
    subtitle: "Even rockstars need a break from rocking!",
  },
  {
    emoji: "🎸",
    title: "Guitar strings need rest too",
    subtitle: "They've been working overtime lately...",
  },
  {
    emoji: "☕",
    title: "Probably sipping Coffee somewhere",
    subtitle: "Good music needs good caffeine, you know?",
  },
  {
    emoji: "🌴",
    title: "Gone to find new songs",
    subtitle: "The best melodies hide in unexpected places!",
  },
  {
    emoji: "🎤",
    title: "Mic is taking a power nap",
    subtitle: "Shh... it's dreaming of the next gig!",
  },
  {
    emoji: "🌙",
    title: "Recharging creative batteries",
    subtitle: "Great performances need great preparation!",
  },
  {
    emoji: "🎵",
    title: "Notes are on a field trip",
    subtitle: "They'll be back with new harmonies soon!",
  },
  {
    emoji: "🦋",
    title: "Chasing butterflies & melodies",
    subtitle: "Both are equally hard to catch, btw.",
  },
  {
    emoji: "📚",
    title: "Learning a secret new song",
    subtitle: "Spoiler: It's gonna be amazing!",
  },
  {
    emoji: "🌈",
    title: "Finding inspiration in the clouds",
    subtitle: "The next setlist is being curated!",
  },
];

const WHATSAPP_LINK = "https://wa.me/message/BDUVQZ6MXENFB1";

/**
 * Empty State Component - Creative "No Shows" display
 */
const EmptyState = ({ month }) => {
  // Pick a truly random quote on each page load (stable during the component's lifetime)
  const [quote] = React.useState(() => {
    const randomIndex = Math.floor(Math.random() * NO_SHOWS_QUOTES.length);
    return NO_SHOWS_QUOTES[randomIndex];
  });

  return (
    <div className="flex flex-col items-center justify-center py-10 px-6 text-center">
      {/* Animated emoji container */}
      <div className="relative mb-5">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/30 to-neon-pink/30 blur-2xl rounded-full animate-pulse" />
        <div className="relative flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-white/[0.08] to-white/[0.02] border border-white/10 shadow-lg">
          <span className="text-5xl animate-bounce" style={{ animationDuration: '2s' }}>
            {quote.emoji}
          </span>
        </div>
      </div>

      {/* Quote content */}
      <h3 className="text-white text-xl font-bold mb-2 leading-snug">
        {quote.title}
      </h3>
      <p className="text-[#90a9cb] text-sm mb-6 max-w-[280px] leading-relaxed">
        {quote.subtitle}
      </p>

      {/* WhatsApp CTA */}
      <a
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noopener noreferrer"
        className="group relative overflow-hidden w-full max-w-[280px] flex items-center justify-center gap-2.5 py-3.5 px-6 bg-gradient-to-r from-[#25D366]/20 to-[#128C7E]/20 hover:from-[#25D366]/30 hover:to-[#128C7E]/30 border border-[#25D366]/30 rounded-xl transition-all duration-300"
      >
        {/* Glow effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#25D366]/0 via-[#25D366]/10 to-[#128C7E]/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* WhatsApp Icon */}
        <svg 
          className="w-5 h-5 text-[#25D366] flex-shrink-0 relative z-10" 
          viewBox="0 0 24 24" 
          fill="currentColor"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        
        <span className="text-[#25D366] text-sm font-semibold relative z-10">
          Ping on WhatsApp
        </span>
        
        <Icon name="arrow_forward" size="sm" className="text-[#25D366]/70 group-hover:translate-x-1 transition-transform relative z-10" />
      </a>

      {/* Playful hint */}
      <p className="text-[#90a9cb]/60 text-xs mt-4 max-w-[240px] italic">
        ...or gently remind her to update the calendar 😉
      </p>
    </div>
  );
};

/**
 * Loading Skeleton
 */
const LoadingSkeleton = () => (
  <div className="flex flex-col gap-3">
    {[1, 2, 3].map((i) => (
      <div key={i} className="flex gap-4 p-4 rounded-2xl bg-white/5 border border-white/5 animate-pulse">
        <div className="w-[60px] h-[72px] rounded-xl bg-white/10" />
        <div className="flex-1 flex flex-col gap-2">
          <div className="h-5 w-3/4 rounded bg-white/10" />
          <div className="h-4 w-1/2 rounded bg-white/10" />
          <div className="h-3 w-1/3 rounded bg-white/10" />
        </div>
      </div>
    ))}
  </div>
);

/**
 * Performances Page - Shows where Matisa is performing this month
 */
const Performances = () => {
  const navigate = useNavigate();
  const { 
    currentMonthEvents, 
    nextMonthEvents, 
    isLoading, 
    error, 
    currentMonthName, 
    nextMonthName 
  } = usePerformanceEvents();

  // Page-specific meta information
  const pageTitle = "Live This Month | Matisa - Singer & Guitarist";
  const pageDescription = "Catch Matisa performing live! Check out upcoming show dates, venues, and locations for this month.";

  const totalUpcoming = currentMonthEvents.length + nextMonthEvents.length;

  return (
    <div className="w-full max-w-[480px] min-h-screen-safe bg-background-dark shadow-2xl overflow-hidden flex flex-col relative mx-auto">
      {/* Page Meta Tags - React 19 hoists these to <head> */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {/* Open Graph / Social Media Meta Tags */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://matisa.in/live" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content="https://matisa.in/hero_main.png" />
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content="https://matisa.in/hero_main.png" />
      
      {/* Sticky Header with Menu */}
      <PageHeader title="Matisa" />

      {/* Main Content Scroll Area */}
      <main className="flex-1 overflow-y-auto scrollbar-hide">
        {/* Hero Section */}
        <section className="relative px-6 pt-6 pb-4">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent pointer-events-none" />
          
          <div className="relative">
            {/* Title with animated icon */}
            <div className="flex items-center gap-3 mb-2">
              <div className="relative">
                <div className="absolute inset-0 bg-neon-pink/30 blur-lg animate-pulse-glow" />
                <div className="relative flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-neon-pink/30 to-primary/30 border border-white/10">
                  <Icon name="nightlife" size="md" className="text-white" />
                </div>
              </div>
              <h1 className="text-white text-3xl font-bold leading-tight tracking-tight">
                Live This Month
              </h1>
            </div>

            {/* Subtitle with stats */}
            <p className="text-[#90a9cb] text-sm font-medium pl-[52px]">
              {isLoading ? (
                'Loading upcoming shows...'
              ) : totalUpcoming > 0 ? (
                <>
                  <span className="text-gold font-bold">{totalUpcoming}</span>
                  {totalUpcoming === 1 ? ' show' : ' shows'} coming up
                </>
              ) : (
                'Check back for upcoming performances'
              )}
            </p>
          </div>
        </section>

        {/* Error State */}
        {error && (
          <div className="mx-6 mb-4 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
            <div className="flex items-center gap-2 text-red-400">
              <Icon name="error" size="sm" />
              <span className="text-sm font-medium">Unable to load events</span>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <section className="px-6 pb-6">
            <LoadingSkeleton />
          </section>
        )}

        {/* Current Month Section */}
        {!isLoading && (
          <section className="px-6 pb-6">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="calendar_today" size="sm" className="text-gold" />
              <h2 className="text-white text-lg font-bold tracking-tight">{currentMonthName}</h2>
              {currentMonthEvents.length > 0 && (
                <span className="ml-auto px-2 py-0.5 text-xs font-bold text-gold bg-gold/20 rounded-full">
                  {currentMonthEvents.length} {currentMonthEvents.length === 1 ? 'show' : 'shows'}
                </span>
              )}
            </div>

            {currentMonthEvents.length > 0 ? (
              <div className="flex flex-col gap-3">
                {currentMonthEvents.map((event, index) => (
                  <EventCard key={event.uid || index} event={event} index={index} />
                ))}
              </div>
            ) : (
              <EmptyState month={currentMonthName} />
            )}
          </section>
        )}

        {/* Divider */}
        {!isLoading && nextMonthEvents.length > 0 && (
          <div className="mx-6 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        )}

        {/* Next Month Section */}
        {!isLoading && nextMonthEvents.length > 0 && (
          <section className="px-6 py-6">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="event_upcoming" size="sm" className="text-primary" />
              <h2 className="text-white text-lg font-bold tracking-tight">Coming in {nextMonthName}</h2>
              <span className="ml-auto px-2 py-0.5 text-xs font-bold text-primary bg-primary/20 rounded-full">
                {nextMonthEvents.length} {nextMonthEvents.length === 1 ? 'show' : 'shows'}
              </span>
            </div>

            <div className="flex flex-col gap-3">
              {nextMonthEvents.map((event, index) => (
                <EventCard key={event.uid || index} event={event} index={index} />
              ))}
            </div>
          </section>
        )}

        {/* Book a Show CTA Section */}
        <section className="px-6 pb-24">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gold/10 via-transparent to-primary/10 border border-white/10 p-5">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gold/10 blur-3xl rounded-full -translate-y-1/2 translate-x-1/2" />
            
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="celebration" size="sm" className="text-gold" />
                <h3 className="text-white text-lg font-bold">Want a Private Show?</h3>
              </div>
              <p className="text-[#90a9cb] text-sm mb-4 pl-6">
                Book Matisa for your wedding, corporate event, or private party
              </p>
              <button 
                onClick={() => {
                  const scrollContainer = document.querySelector('.overflow-y-auto');
                  if (scrollContainer) scrollContainer.scrollTo(0, 0);
                  navigate('/enquiry');
                }}
                className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-gold/20 to-gold/10 hover:from-gold/30 hover:to-gold/20 border border-gold/30 rounded-xl text-gold text-sm font-semibold transition-all"
              >
                <Icon name="send" size="sm" />
                Book Now
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* Floating Action Button */}
      <FloatingActionButton to="/enquiry" />

      {/* Bottom Blur Overlay */}
      <div className="pointer-events-none fixed bottom-0 left-0 h-12 w-full bg-gradient-to-t from-background-dark to-transparent lg:absolute" />
    </div>
  );
};

export default Performances;
